import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { queryKeys } from './queryKeys';

export function useCreateContact() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactData) => {
      const response = await authFetch('/api/crm/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create contact');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.contacts });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.stats });
    },
  });
}

export function useUpdateContact() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contactId, ...updateData }) => {
      const response = await authFetch(`/api/crm/contact?id=${contactId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update contact');
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.contact(variables.contactId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.contacts });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.stats });
    },
  });
}

export function useDeleteContact() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactId) => {
      const response = await authFetch(`/api/crm/contact?id=${contactId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete contact');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.contacts });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.stats });
    },
  });
}

export function useAddContactNote() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contactId, content }) => {
      const response = await authFetch('/api/crm/contact-notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactId, content }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add note');
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.contact(variables.contactId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.contacts });
    },
  });
}


// Opportunity mutations
export function useCreateOpportunity() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (opportunityData) => {
      const response = await authFetch('/api/crm/opportunities', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(opportunityData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create opportunity');
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.opportunities });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.contact(variables.contactId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.stats });
    },
  });
}

export function useUpdateOpportunity() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ opportunityId, ...updateData }) => {
      const response = await authFetch(`/api/crm/opportunity?id=${opportunityId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update opportunity');
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.opportunity(variables.opportunityId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.opportunities });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.contacts });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.stats });
    },
  });
}

export function useDeleteOpportunity() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (opportunityId) => {
      const response = await authFetch(`/api/crm/opportunity?id=${opportunityId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete opportunity');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.opportunities });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.contacts });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.stats });
    },
  });
}

export function useAddInteraction() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contactId, opportunityId, interactionType, subject, content, interactionDate, durationMinutes }) => {
      const response = await authFetch('/api/crm/interactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactId, opportunityId, interactionType, subject, content, interactionDate, durationMinutes }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add interaction');
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      if (variables.contactId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.crm.contact(variables.contactId) });
      }
      if (variables.opportunityId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.crm.opportunity(variables.opportunityId) });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.contacts });
    },
  });
}

export function useCreateSourceOption() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ value }) => {
      // Value is now the human-readable name (e.g., "LinkedIn Referral")
      const response = await authFetch('/api/crm/source-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: value.trim() }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create source option');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.sourceOptions });
    },
  });
}
